import type { UploadTask } from '../uploader/UploadTask';
import type { UploadTaskSnapshot } from '../uploader/types';
import { formatBytes, formatDuration, formatEta, formatSpeed, truncateMiddle } from '../utils/format';

export interface FileCardHandlers {
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  onRemove: () => void;
}

const STATUS_LABELS: Record<UploadTaskSnapshot['status'], string> = {
  waiting: 'Waiting',
  preparing: 'Preparing…',
  uploading: 'Uploading',
  paused: 'Paused',
  retrying: 'Retrying…',
  completing: 'Completing…',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

function icon(path: string): string {
  return `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="${path}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

const ICONS = {
  pause: icon('M8 5v14M16 5v14'),
  resume: icon('M6 4l14 8-14 8V4z'),
  cancel: icon('M6 6l12 12M18 6L6 18'),
  remove: icon('M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6h14z'),
};

export interface FileCardHandle {
  element: HTMLLIElement;
  update: (snapshot: UploadTaskSnapshot) => void;
}

export function createFileCard(task: UploadTask, handlers: FileCardHandlers, template: HTMLTemplateElement): FileCardHandle {
  const fragment = template.content.cloneNode(true) as DocumentFragment;
  const element = fragment.querySelector('.file-card') as HTMLLIElement;

  const nameEl = element.querySelector('.file-name') as HTMLElement;
  const badgeEl = element.querySelector('.file-status-badge') as HTMLElement;
  const fillEl = element.querySelector('.progress-fill') as HTMLElement;
  const primaryMetaEl = element.querySelector('.file-meta-primary') as HTMLElement;
  const secondaryMetaEl = element.querySelector('.file-meta-secondary') as HTMLElement;
  const actionsEl = element.querySelector('.file-actions') as HTMLElement;
  const errorEl = element.querySelector('.file-error') as HTMLElement;

  nameEl.textContent = task.file.name;
  nameEl.title = task.file.name;
  if (task.file.name.length > 46) nameEl.textContent = truncateMiddle(task.file.name);

  function renderActions(snapshot: UploadTaskSnapshot): void {
    actionsEl.innerHTML = '';

    const addButton = (label: string, iconHtml: string, onClick: () => void) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'icon-button';
      button.setAttribute('aria-label', label);
      button.title = label;
      button.innerHTML = iconHtml;
      button.addEventListener('click', onClick);
      actionsEl.appendChild(button);
    };

    switch (snapshot.status) {
      case 'uploading':
      case 'retrying':
        addButton('Pause', ICONS.pause, handlers.onPause);
        addButton('Cancel', ICONS.cancel, handlers.onCancel);
        break;
      case 'paused':
        addButton('Resume', ICONS.resume, handlers.onResume);
        addButton('Cancel', ICONS.cancel, handlers.onCancel);
        break;
      case 'failed':
        addButton('Retry', ICONS.resume, handlers.onResume);
        addButton('Remove', ICONS.remove, handlers.onRemove);
        break;
      case 'waiting':
      case 'preparing':
      case 'completing':
        addButton('Cancel', ICONS.cancel, handlers.onCancel);
        break;
      case 'completed':
      case 'cancelled':
        addButton('Remove', ICONS.remove, handlers.onRemove);
        break;
    }
  }

  let lastStatus: UploadTaskSnapshot['status'] | null = null;

  function update(snapshot: UploadTaskSnapshot): void {
    element.dataset.status = snapshot.status;
    badgeEl.textContent = STATUS_LABELS[snapshot.status];
    fillEl.style.width = `${snapshot.progress.percent.toFixed(1)}%`;

    if (snapshot.status === 'completed') {
      const duration = snapshot.durationSeconds ? ` in ${formatDuration(snapshot.durationSeconds)}` : '';
      primaryMetaEl.textContent = `Upload completed successfully${duration}`;
      secondaryMetaEl.textContent = formatBytes(snapshot.fileSize);
    } else if (snapshot.status === 'cancelled') {
      primaryMetaEl.textContent = 'Upload cancelled';
      secondaryMetaEl.textContent = formatBytes(snapshot.fileSize);
    } else if (snapshot.status === 'failed') {
      primaryMetaEl.textContent = `${formatBytes(snapshot.progress.uploadedBytes)} / ${formatBytes(snapshot.fileSize)}`;
      secondaryMetaEl.textContent = '';
    } else if (snapshot.status === 'uploading' || snapshot.status === 'retrying') {
      primaryMetaEl.textContent = `${formatBytes(snapshot.progress.uploadedBytes)} / ${formatBytes(snapshot.fileSize)} · ${formatSpeed(snapshot.progress.speedBytesPerSecond)}`;
      secondaryMetaEl.textContent = formatEta(snapshot.progress.etaSeconds);
    } else if (snapshot.status === 'paused') {
      primaryMetaEl.textContent = `${formatBytes(snapshot.progress.uploadedBytes)} / ${formatBytes(snapshot.fileSize)}`;
      secondaryMetaEl.textContent = 'Paused';
    } else {
      primaryMetaEl.textContent = formatBytes(snapshot.fileSize);
      secondaryMetaEl.textContent = '';
    }

    if (snapshot.errorMessage) {
      errorEl.hidden = false;
      errorEl.textContent = snapshot.errorMessage;
    } else {
      errorEl.hidden = true;
      errorEl.textContent = '';
    }

    if (snapshot.status !== lastStatus) {
      lastStatus = snapshot.status;
      renderActions(snapshot);
    }
  }

  return { element, update };
}
